import { createEarlyFetchCache } from '@jira/polaris__lib-early-fetch-cache/src/createEarlyFetchCache.tsx';
import { fetchData } from '@jira/polaris__lib-early-fetch-cache/src/fetchData.tsx';
import { waitForGlobalVariable } from '@jira/polaris__lib-early-fetch-cache/src/waitForGlobalVariable.tsx';
import { getEarlyScriptObservabilityCache } from '@jira/polaris__lib-early-fetch-cache/src/getEarlyScriptObservabilityCache.tsx';
import { getProjectIdFromMap } from '@jira/polaris__lib-early-fetch-cache/src/getProjectIdFromMap.tsx';
import { getProjectKeyFromLocation } from '@jira/polaris__lib-early-fetch-cache/src/getProjectKeyFromLocation.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type {
	PermissionsCheckResponse,
	PermissionsResponse,
} from './PermissionsResponse.types.tsx';
import {
	getPermissionsUrl,
	PERMISSIONS_CHECK_PATH,
	buildPermissionsCheckRequestBody,
} from './getPermissionsUrl.tsx';

const earlyFetchCache = createEarlyFetchCache<PermissionsResponse, number>({
	paramsEqualityFn: (paramsA, paramsB) => String(paramsA) === String(paramsB),
});

const earlyFetchCacheForProjects = createEarlyFetchCache<PermissionsCheckResponse, number[]>({
	paramsEqualityFn: (paramsA, paramsB) =>
		paramsA.length === paramsB.length && paramsA.every((id, i) => id === paramsB[i]),
});

/**
 * Gets the cached response and marks it as accessed
 * The cache will be automatically cleared after access or if stale
 */
export const getEarlyFetchResponse = (projectId: number): Promise<PermissionsResponse> | null => {
	return earlyFetchCache.getCachedResponse(projectId);
};

/**
 * Gets the cached multi-project permissions check response and marks it as accessed
 */
export const getEarlyFetchResponseForProjects = (
	projectIds: number[],
): Promise<PermissionsCheckResponse> | null => {
	return earlyFetchCacheForProjects.getCachedResponse(projectIds);
};

/**
 * Helper function to fetch permissions with proper type
 */
const fetchPermissions = (projectId: number): Promise<PermissionsResponse> => {
	const url = getPermissionsUrl(projectId);
	return fetchData<PermissionsResponse>({ url, method: 'GET' });
};

const fetchPermissionsForProjects = (projectIds: number[]): Promise<PermissionsCheckResponse> => {
	return fetchData<PermissionsCheckResponse>({
		url: PERMISSIONS_CHECK_PATH,
		method: 'POST',
		body: JSON.stringify(buildPermissionsCheckRequestBody(projectIds)),
	});
};

/**
 * Fetches permissions data early based on the project id
 */
export const putPermissionsEarlyScriptResponse = (projectId: number): void => {
	if (fg('jpd_multi-project-permission-fetch')) {
		const projectIds = [projectId];
		const response = fetchPermissionsForProjects(projectIds);
		earlyFetchCacheForProjects.setCacheResponse(response, projectIds);
	} else {
		// Execute the actual fetch for permissions
		const response = fetchPermissions(projectId);

		// Store the fetch promise in the cache
		earlyFetchCache.setCacheResponse(response, projectId);
	}
};

/**
 * Starts the early API request for permissions.
 * This function is executed before the main app loads.
 */
export function runGetPermissionsEarlyScript(location: Location): boolean {
	// Parse the location to extract the necessary parameters
	const projectKey = getProjectKeyFromLocation(location);
	if (!projectKey) {
		return false;
	}

	// Use cached projectId if available, otherwise wait for SPA_STATE
	getProjectIdFromMap(projectKey, waitForGlobalVariable)
		.then((projectId) => {
			if (projectId) {
				putPermissionsEarlyScriptResponse(Number(projectId));
			}
		})
		.catch((error) => {
			getEarlyScriptObservabilityCache()?.recordError(
				'polaris.early-fetch-cache.project-id-mapping',
				'Error fetching permissions',
				error instanceof Error ? error : new Error(String(error)),
			);
		});

	return true;
}
